import { QueryResult } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import { useAuthenticatedUser } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { PageType } from '@treams/treams-network';
import {
	Card,
	Container,
	Empty,
	ForwardIcon,
	Jumbo,
	PAGE_CONTENT_WIDTH,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import SharedFeedbackCard from './SharedFeedbackCard';
import ViewerFeedbackButtons from './ViewerFeedbackButtons';
import ViewerFeedbackTabs from './ViewerFeedbackTabs';

const EmptyState = styled(Flex)`
	width: 100%;
	justify-content: center;
	padding-top: ${tokens.gap.page}px;

	> * {
		max-width: none;
	}
`;

function SharedFeedbackList(props: { query: QueryResult<graphql.FeedbackSharedWithUserQuery> }) {
	const { t } = useTranslation();

	return (
		<Container mb={3} mt="48px" wide={PAGE_CONTENT_WIDTH}>
			{props.query.data ? (
				props.query.data.user.sharedFeedback.edges.length > 0 ? (
					<Card p={0}>
						{props.query.data.user.sharedFeedback.edges
							.slice()
							.reverse()
							.map(({ node }) => (
								<SharedFeedbackCard key={node.request.id} {...node} />
							))}
					</Card>
				) : (
					<EmptyState>
						<Empty
							icon={<ForwardIcon />}
							title={t('feedback.shared.empty.title')}
							description={t('feedback.shared.empty.description')}
						/>
					</EmptyState>
				)
			) : (
				<Skeleton.Repeat count={10}>
					<SharedFeedbackCard.Skeleton />
				</Skeleton.Repeat>
			)}
		</Container>
	);
}

export default function SharedFeedback() {
	const { t } = useTranslation();

	const viewer = useAuthenticatedUser();
	const userID = viewer.id;

	const query = graphql.useFeedbackSharedWithUserQuery({ variables: { userID } });

	return (
		<>
			<Jumbo.Editable
				hasMediaUpload
				page={PageType.FeedbackShared}
				title={t('feedback.shared.title')}
				description={t('feedback.shared.description')}
				button={<ViewerFeedbackButtons />}
				footer={<ViewerFeedbackTabs />}
			/>
			<SharedFeedbackList query={query} />
		</>
	);
}
